import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type MentionsByPlatformView = {
  /** `Top channel`, `Most negative by volume`, `Most negative by rate`. */
  kpis: KPIMetric[];
  /** One row per channel, ranked by mentions; one key per sentiment. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type MentionsByPlatformCardProps = WidgetBlockProps & {
  view?: MentionsByPlatformView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:18142` — which channels carry the conversation, and the mood on each.
 *
 * Counts, not shares. The card's own figures ask both questions — "most
 * negative by volume" and "most negative by rate" — so the bars carry the
 * volume and the rate is the reading the KPI adds to it. A normalised plot
 * would answer the second twice and the first not at all. A channel below the
 * rate floor (3 mentions) can top the chart but cannot be named most negative
 * by rate.
 */
export function MentionsByPlatformCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: MentionsByPlatformCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Mentions by platform and sentiment"
      helpText="Mention counts on each platform, split into positive, neutral and negative sentiment. Larger bars indicate more conversation volume."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
